// Phase 14 — Affiliate Product Picker
// Phase 17 — adds commission % display, copy-link, QR per item.
// Searchable inventory picker with images, availability, compliance.
import React, { useState, useEffect, useCallback } from "react";
import {
  Search, Truck, Package, ShieldAlert, ShieldCheck, ImageIcon, Loader2,
  Link2, Copy, QrCode, Check, X,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { ProductImg } from "@/lib/productImage";

const SOURCE_BADGE = {
  admin:           "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  partner:         "bg-sky-500/15 text-sky-300 border-sky-500/40",
  franchisee:      "bg-rose-500/15 text-rose-300 border-rose-500/40",
  lifted_supplies: "bg-amber-400/15 text-amber-300 border-amber-400/40",
  imported_legacy: "bg-white/10 text-white/60 border-white/20",
};

// Phase 17 — display commission rules sourced from /api/admin/commission-config
// (we don't fetch here — the picker shows the canonical defaults so the
// affiliate knows what to expect: 10% storefront, 50% affiliate program).
function commissionFor(product) {
  const handle = (product?.handle || "").toLowerCase();
  if (
    handle.startsWith("lift-off") ||
    handle.startsWith("liftoff") ||
    handle.startsWith("lifted-affiliate")
  ) return { pct: 50, label: "Affiliate program · 50%" };
  return { pct: 10, label: "Storefront · 10%" };
}

function qrUrl(target, size = 220) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=10&data=${encodeURIComponent(target)}`;
}

// Phase 19 — backend now returns relative paths so we can join them
// with the actual app origin (preview vs production). Falls back to the
// raw referral_url field if a relative path isn't present.
function absoluteFromLink(link) {
  if (!link) return "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const path = link.short_path || link.referral_path
    || (link.short_url && link.short_url.startsWith("/") ? link.short_url : null)
    || (link.referral_url && link.referral_url.startsWith("/") ? link.referral_url : null)
    || link.referral_url || link.short_url || "";
  // If the backend (legacy) still returned an absolute URL, just use it.
  if (/^https?:\/\//i.test(path)) return path;
  return `${origin}${path.startsWith("/") ? "" : "/"}${path}`;
}

// Robust clipboard write — falls back to a hidden textarea + execCommand
// when navigator.clipboard isn't available (older browsers / insecure
// iframes / Brave with permission blocked).
async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) { return false; }
}

export default function AffiliateProductPicker({ onLinkCreated, defaultZip = "" }) {
  const [q, setQ] = useState("");
  const [zip, setZip] = useState(defaultZip);
  const [sameDayOnly, setSameDayOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState("");
  const [copiedHandle, setCopiedHandle] = useState("");
  const [qrFor, setQrFor] = useState(null);
  const [createdLinks, setCreatedLinks] = useState({});  // {handle: link}

  const search = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const params = { limit: 20 };
      if (q) params.q = q;
      if (zip) params.zip = zip;
      if (sameDayOnly) params.same_day = true;
      const { data } = await api.get("/affiliate/me/products/search", { params });
      setItems(data.items || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  }, [q, zip, sameDayOnly]);

  useEffect(() => {
    const t = setTimeout(search, 350);   // debounce
    return () => clearTimeout(t);
  }, [search]);

  const createLink = async (product, sameDay = false) => {
    setCreating(product.handle + (sameDay ? ":sd" : ""));
    setErr("");
    try {
      const { data } = await api.post("/affiliate/me/product-links/create", {
        product_handle: product.handle,
        product_id: product.product_id,
        product_slug: product.handle,
        destination_url: sameDay
          ? `/shop/same-day/${product.handle}`
          : `/products/${product.handle}`,
        active: true,
        same_day: sameDay,
      });
      const link = data.link;
      setCreatedLinks((s) => ({ ...s, [product.handle + (sameDay ? ":sd" : "")]: link }));
      onLinkCreated?.(link);
      return link;
    } catch (e) { setErr(apiErrorMessage(e)); return null; }
    finally { setCreating(""); }
  };

  const copyLink = async (product, sameDay = false) => {
    const key = product.handle + (sameDay ? ":sd" : "");
    let link = createdLinks[key];
    if (!link) link = await createLink(product, sameDay);
    if (!link) return;
    const absolute = absoluteFromLink(link);
    const ok = await copyToClipboard(absolute);
    if (ok) {
      setCopiedHandle(key);
      setTimeout(() => setCopiedHandle(""), 1500);
    } else {
      // Fallback — surface the URL via prompt so the user can copy manually
      setErr("Clipboard blocked. Manually copy: " + absolute);
    }
  };

  const showQr = async (product, sameDay = false) => {
    const key = product.handle + (sameDay ? ":sd" : "");
    let link = createdLinks[key];
    if (!link) link = await createLink(product, sameDay);
    if (!link) return;
    setQrFor({
      id: link.id,
      label: product.title,
      url: absoluteFromLink(link),
    });
  };

  const openTestLink = async (product, sameDay = false) => {
    const key = product.handle + (sameDay ? ":sd" : "");
    let link = createdLinks[key];
    if (!link) link = await createLink(product, sameDay);
    if (!link) return;
    window.open(absoluteFromLink(link), "_blank", "noopener");
  };

  return (
    <div className="glass-panel p-4" data-testid="affiliate-product-picker">
      <div className="flex items-center gap-2 mb-3">
        <Package className="h-4 w-4 text-lifted-green" />
        <div className="font-bebas tracking-[0.22em] text-xs text-lifted-dim uppercase">
          Product Picker · Real Lifted Inventory
        </div>
      </div>

      {/* Search controls */}
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-lifted-dim" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products by title, tag, vendor"
                 className="w-full bg-black/40 border border-white/10 rounded-md pl-7 pr-2 py-2 text-sm"
                 data-testid="picker-search" />
        </div>
        <input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="ZIP" maxLength={10}
               className="w-24 bg-black/40 border border-white/10 rounded-md px-2 py-2 text-sm"
               data-testid="picker-zip" />
        <label className="text-xs font-bebas tracking-[0.18em] text-lifted-dim uppercase flex items-center gap-1.5">
          <input type="checkbox" checked={sameDayOnly} onChange={(e) => setSameDayOnly(e.target.checked)}
                 className="accent-lifted-green" data-testid="picker-same-day-only" />
          Same-day only
        </label>
      </div>

      {err && <div className="text-rose-300 text-xs mb-2" data-testid="picker-err">{err}</div>}

      {/* Results */}
      {busy ? (
        <div className="text-center text-sm text-lifted-dim p-6">
          <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Searching…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center text-sm text-lifted-dim p-6" data-testid="picker-empty">
          No products match. Try a broader search.
        </div>
      ) : (
        <ul className="divide-y divide-white/5 max-h-[520px] overflow-y-auto" data-testid="picker-results">
          {items.map((p) => {
            const commission = commissionFor(p);
            const linkKey = p.handle;
            return (
              <li key={p.handle} className="py-3 flex items-start gap-3" data-testid={`picker-item-${p.handle}`}>
                <div className="h-16 w-16 rounded bg-black/40 overflow-hidden border border-white/10 grid place-items-center shrink-0">
                  {(p.image_url || (p.images && p.images.length)) ? (
                    <ProductImg product={p} className="h-full w-full object-cover" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-lifted-dim" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{p.title}</div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    <span className="text-sm text-lifted-green font-bebas tracking-wide">
                      ${((p.price_cents || 0) / 100).toFixed(2)}
                    </span>
                    <span
                      className="px-2 py-0.5 rounded border border-lifted-green/40 bg-lifted-green/10 text-lifted-green text-[0.7rem] font-bebas tracking-[0.18em] uppercase"
                      data-testid={`picker-commission-${p.handle}`}
                    >
                      {commission.label}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded border text-[0.65rem] font-bebas tracking-[0.18em] uppercase ${SOURCE_BADGE[p.source_type] || SOURCE_BADGE.imported_legacy}`}>
                      {(p.source_type || "legacy").replace("_", " ")}
                    </span>
                    {p.same_day_available && (
                      <span className="px-1.5 py-0.5 rounded border border-amber-400/40 bg-amber-400/10 text-amber-300 text-[0.65rem] font-bebas tracking-[0.18em] uppercase inline-flex items-center gap-1">
                        <Truck className="h-3 w-3" /> Same-day
                      </span>
                    )}
                    {p.shipping_available && (
                      <span className="px-1.5 py-0.5 rounded border border-white/15 text-white/70 text-[0.65rem] font-bebas tracking-[0.18em] uppercase">
                        Ships
                      </span>
                    )}
                    {p.state_blocked && (
                      <span className="px-1.5 py-0.5 rounded border border-rose-400/40 bg-rose-400/10 text-rose-300 text-[0.65rem] font-bebas tracking-[0.18em] uppercase inline-flex items-center gap-1">
                        <ShieldAlert className="h-3 w-3" /> Restricted
                      </span>
                    )}
                    {!p.state_blocked && p.compliance_status === "compliant" && (
                      <span className="text-[0.65rem] text-lifted-green inline-flex items-center gap-1">
                        <ShieldCheck className="h-3 w-3" /> Verified
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button onClick={() => createLink(p, false)} disabled={creating === p.handle}
                          className="btn-ghost text-xs inline-flex items-center justify-center gap-1 disabled:opacity-50 min-w-[90px]"
                          data-testid={`picker-link-${p.handle}`}>
                    {creating === p.handle ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
                    Link
                  </button>
                  <div className="flex gap-1">
                    <button onClick={() => copyLink(p, false)}
                            className="btn-ghost text-xs inline-flex items-center justify-center gap-1 flex-1"
                            data-testid={`picker-copy-${p.handle}`}>
                      {copiedHandle === linkKey ? <Check className="h-3 w-3 text-lifted-green" /> : <Copy className="h-3 w-3" />}
                      {copiedHandle === linkKey ? "Copied" : "Copy"}
                    </button>
                    <button onClick={() => showQr(p, false)}
                            className="btn-ghost text-xs inline-flex items-center justify-center gap-1"
                            data-testid={`picker-qr-${p.handle}`}>
                      <QrCode className="h-3 w-3" />
                    </button>
                  </div>
                  {createdLinks[linkKey] && (
                    <button onClick={() => openTestLink(p, false)}
                            className="text-[0.65rem] text-lifted-green underline hover:text-lifted-green/80 inline-flex items-center justify-center gap-1"
                            data-testid={`picker-open-${p.handle}`}>
                      Open test link
                    </button>
                  )}
                  {p.same_day_available && (
                    <button onClick={() => createLink(p, true)} disabled={creating === `${p.handle}:sd`}
                            className="btn-ghost text-xs inline-flex items-center justify-center gap-1 disabled:opacity-50"
                            data-testid={`picker-sameday-${p.handle}`}>
                      {creating === `${p.handle}:sd` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Truck className="h-3 w-3" />}
                      Same-day
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Phase 19 — once any link has been generated, surface the latest
          absolute URL as a copy-friendly readonly input so the affiliate
          can verify the destination before sharing. */}
      {Object.keys(createdLinks).length > 0 && (
        <div className="mt-3 glass-panel p-3" data-testid="picker-last-link">
          <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-1">
            Last generated link
          </div>
          <div className="flex gap-2 items-center">
            <input
              readOnly
              className="flex-1 bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs font-mono"
              value={absoluteFromLink(Object.values(createdLinks).slice(-1)[0])}
              data-testid="picker-last-link-input"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={async () => {
                const last = Object.values(createdLinks).slice(-1)[0];
                if (!last) return;
                const ok = await copyToClipboard(absoluteFromLink(last));
                if (ok) {
                  setCopiedHandle("__last__");
                  setTimeout(() => setCopiedHandle(""), 1500);
                }
              }}
              className="btn-ghost text-xs inline-flex items-center gap-1"
              data-testid="picker-last-link-copy"
            >
              {copiedHandle === "__last__" ? <Check className="h-3 w-3 text-lifted-green" /> : <Copy className="h-3 w-3" />}
              {copiedHandle === "__last__" ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {/* QR modal */}
      {qrFor && (
        <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4"
             onClick={() => setQrFor(null)} data-testid="picker-qr-modal">
          <div className="glass-panel p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-bebas tracking-[0.22em] text-xs text-lifted-green uppercase">
                QR · {qrFor.label}
              </div>
              <button onClick={() => setQrFor(null)} className="text-lifted-dim hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <img src={qrUrl(qrFor.url, 280)} alt="QR" className="mx-auto rounded-md bg-white p-2" />
            <div className="font-mono text-xs text-lifted-dim mt-2 break-all">{qrFor.url}</div>
          </div>
        </div>
      )}
    </div>
  );
}
